import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Button,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Snackbar,
  Alert,
  Tooltip,
  Link,
  Chip,
  Collapse,
  IconButton,
  LinearProgress,
  Drawer,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { useNavigate, useParams } from 'react-router-dom';
import { IssueQuestionDetails } from '../../types/question';
import { getQuestionTypeName } from '../../types/questionTypeMap';
import { PERMISSIONS } from '../../constants/permissions';
import { fetchData, postData, putData } from '../../utils/apiClient';
import env from '../../env';
import Cookies from 'universal-cookie';
import { QAP_COOKIE } from '../../constants';
import { LoadingIndicator } from '../LoadingIndicator';
import ConfirmationDialog from '../Dialogbox/ConfirmationDialog';
import { getQuestionRestrictionLabel } from '../../constants/questionOptions';
import { useFeatureFlag } from '../../provider/AppConfigProvider';

interface IssueDetailsCardProps {
  issueData: IssueQuestionDetails | null;
  permissions: string[];
  loading?: boolean;
  onDataRefresh?: () => void;
}

const cookies = new Cookies();

function getToken(): string | null {
  const QAP_COOKIE_NAME = QAP_COOKIE + '_' + env.ENVIRONMENT;
  return cookies.get(QAP_COOKIE_NAME);
}

/** Renders detail fields: null, undefined, blank, or NaN numeric → N/A */
function formatFieldDisplay(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return 'N/A';
  if (typeof value === 'number') {
    if (Number.isNaN(value)) return 'N/A';
    return String(value);
  }
  if (value.trim() === '') return 'N/A';
  return value;
}

const IssueDetailsCard: React.FC<IssueDetailsCardProps> = ({
  issueData,
  permissions,
  loading = false,
  onDataRefresh,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPreviewBtnEnable, setCurrentPreviewBtnEnable] = useState(true);
  const [wipPreviewBtnEnable, setWipPreviewBtnEnable] = useState(true);
  const [svnSaveBtnEnable, setSvnSaveBtnEnable] = useState(true);
  const [svnReloadBtnEnable, setSvnReloadBtnEnable] = useState(true);
  const [validateQuestionBtnEnable, setValidateQuestionBtnEnable] = useState(false);
  const { jiraId: jiraIdFromParams } = useParams<{
    jiraId: string;
  }>();

  // Confirmation dialog states
  const [confirmSaveToSVN, setConfirmSaveToSVN] = useState(false);
  const [confirmSaveToWAS, setConfirmSaveToWAS] = useState(false);
  const [confirmReloadSVN, setConfirmReloadSVN] = useState(false);
  const featureFlagValidator = useFeatureFlag('question-validator', false);
  const featureFlagTimeTracking = useFeatureFlag('time-tracking', false);
  const [timeTrackingExpanded, setTimeTrackingExpanded] = useState(true);
  const [timeAbsoluteTrackingExpanded, setTimeAbsoluteTrackingExpanded] = useState(true);
  const [timeTrackingDrawerOpen, setTimeTrackingDrawerOpen] = useState(false);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const hasPermission = (permission: string): boolean => {
    return permissions.includes(permission);
  };

  const getWASSaveButtonStatus = (): boolean => {
    if (!issueData) return false;
    const wasVersion = Number(issueData.wasVersion) ?? 0;
    const svnVersion = Number(issueData.svnVersion) ?? 0;
    return wasVersion < svnVersion;
  }

  // Check package availability to enable validate button
  useEffect(() => {
    if (featureFlagValidator) {
      const checkPackageAvailability = async () => {
        if (!issueData?.jiraId) {
          setValidateQuestionBtnEnable(false);
          return;
        }

        try {
          const response = await fetchData(
            `/jira/${issueData.jiraId}/package-availability`,
            PERMISSIONS.CHECK_PACKAGE_AVAILABILITY,
          );

          if (response && response.available === true) {
            setValidateQuestionBtnEnable(true);
          } else {
            setValidateQuestionBtnEnable(false);
          }
        } catch (error) {
          console.error('Failed to check package availability:', error);
          setValidateQuestionBtnEnable(false);
        }
      };

      checkPackageAvailability();
    }
  }, [issueData?.jiraId, featureFlagValidator]);


  const handleStatusUpdate = async () => {
    if (!issueData) return;

    setIsUpdating(true);
    try {
      const newStatus = issueData.status === 'Open' ? 'CLOSE' : 'OPEN';

      await putData(
        '/issues/status',
        PERMISSIONS.UPDATE_ISSUE_STATUS,
        {
          jiraId: issueData.jiraId,
          status: newStatus,
        },
      );

      // Close dialog and refresh data
      setIsDialogOpen(false);
      if (onDataRefresh) {
        onDataRefresh();
      }

      // Show success message
      setSnackbar({
        open: true,
        message: `Issue status successfully updated to ${newStatus}`,
        severity: 'success',
      });
    } catch (error) {
      console.error('Failed to update issue status:', error);

      // Show error message
      setSnackbar({
        open: true,
        message: 'Failed to update issue status. Please try again.',
        severity: 'error',
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleStatusButtonClick = () => {
    setIsDialogOpen(true);
  };

  const handleDialogClose = () => {
    if (!isUpdating) {
      setIsDialogOpen(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleSaveToWASClick = () => {
    setConfirmSaveToWAS(true);
  };

  const handleSaveToWAS = async () => {
    setConfirmSaveToWAS(false);
    setIsLoading(true);
    try {

      if (!issueData || !issueData.jiraId || !issueData.wasQuestionId) {
        console.error('Insufficient question data to launch WAS Preview');
        setSnackbar({
          open: true,
          message: 'Failed to save to WAS. Missing JIRA ID or WAS Question ID.',
          severity: 'error',
        });
        return;
      }
      console.log('Launching WAS publish for question:', issueData);

      // Make GET request to backend to get preview URL
      const response = await fetchData(
        `/was/publish?j=${issueData.jiraId}&q=${issueData.wasQuestionId}&i=${issueData.issueId}`,
        PERMISSIONS.SAVE_QUESTION_WAS,
      );

      if (response.status && response.status.status === "OK") {
        console.log("Changes saved to WAS successfully!");
        setSnackbar({
          open: true,
          message: 'Changes saved to WAS successfully!',
          severity: 'success',
        });
        // Refresh page data after a short delay to allow snackbar to be visible
        if (onDataRefresh) {
          setTimeout(() => {
            onDataRefresh();
          }, 2000);
        }
      } else {
        throw new Error('No response received. Please Try again later.');
      }
    } catch (error) {
      console.error('Error:', error);
      setSnackbar({
        open: true,
        message: 'WAS Question Save Failed!',
        severity: 'error',
      });
    } finally {
      setIsLoading(false)
    }
  };

  const handleSaveToSVNClick = () => {
    setConfirmSaveToSVN(true);
  };

  const handleSaveToSVN = async () => {
    setConfirmSaveToSVN(false);
    setSvnSaveBtnEnable(false);
    setIsLoading(true);
    try {

      if (!issueData || !issueData.jiraId || !issueData.wasQuestionId) {
        console.error('Insufficient question data to Save in SVN');
        setSnackbar({
          open: true,
          message: '',
          severity: 'error',
        });
        return;
      }
      console.log('Launching Save to SVN:', issueData);

      // Make POST request to backend
      const data = {
        "jiraId": issueData.jiraId,
        "issueId": issueData.issueId,
        "questionId": issueData.wasQuestionId
      }

      const response = await postData(
        "/svn/save",
        PERMISSIONS.SAVE_QUESTION_SVN,
        data
      );

      console.log(response)

      if (response) {
        console.log("Saved to SVN Successfully");
        setSnackbar({
          open: true,
          message: 'Changes saved to SVN successfully!',
          severity: 'success',
        });
        // Refresh page data after a short delay to allow snackbar to be visible
        if (onDataRefresh) {
          setTimeout(() => {
            onDataRefresh();
          }, 2000);
        }
      } else {
        throw new Error('No response received. Please Try again later.');
      }
    } catch (error) {
      console.error('Error:', error);

      const error_message = error instanceof Error ? error.message : String(error)
      let snackbar_message = 'Save to  Failed!';
      if (error_message === 'No Changes Detected!') {
        snackbar_message = "No changes to save. Your files are already up to date in SVN."
      } else {
        snackbar_message = `Save to SVN Failed! : ${error_message}`
      }
      setSnackbar({
        open: true,
        message: snackbar_message,
        severity: 'error',
      });
    } finally {
      setIsLoading(false)
      setSvnSaveBtnEnable(true);
    }
  };

  const handleReloadSVNClick = () => {
    setConfirmReloadSVN(true);
  };

  const handleReloadSVN = async () => {
    setConfirmReloadSVN(false);
    setSvnReloadBtnEnable(false);
    setIsLoading(true);
    try {

      if (!issueData || !issueData.jiraId || !issueData.wasQuestionId) {
        console.error('Insufficient question data to Reload from SVN');
        setSnackbar({
          open: true,
          message: '',
          severity: 'error',
        });
        return;
      }
      console.log('Ready to Reload from SVN:', issueData);

      // Make GET request to backend
      const response = await fetchData(
        `/svn/reload?i=${issueData.jiraId}&q=${issueData.wasQuestionId}`,
        PERMISSIONS.RELOAD_QUESTION_FROM_SVN,
      );

      console.log(response)

      if (response) {
        console.log("Reload from SVN Successfully");
        setSnackbar({
          open: true,
          message: 'Reload from SVN Successfully!',
          severity: 'success',
        });
      } else {
        throw new Error('No response received. Please Try again later.');
      }
    } catch (error) {
      console.error('Error:', error);
      setSnackbar({
        open: true,
        message: 'Reload from Failed!',
        severity: 'error',
      });
    } finally {
      setIsLoading(false)
      setSvnReloadBtnEnable(true);
    }
  };

  const handleWasPreview = async (isOriginal: boolean) => {
    // loading time indicators
    setIsLoading(true);
    if (isOriginal) {
      setWipPreviewBtnEnable(false);
    } else {
      setCurrentPreviewBtnEnable(false);
    }

    if (!issueData) return;

    if (!issueData.jiraId || !issueData.wasQuestionId) {
      console.error('Insufficient question data to launch WAS Preview');
      setSnackbar({
        open: true,
        message: 'Failed to launch WAS Preview. Missing JIRA ID or WAS Question ID.',
        severity: 'error',
      });
      return;
    }
    console.log('Launching WAS Preview for question:', issueData);

    // Make GET request to backend to get preview URL
    try {
      const response = await fetchData(
        `/was/preview?i=${issueData.jiraId}&q=${issueData.wasQuestionId}&original=${isOriginal}`,
        PERMISSIONS.WAS_PREVIEW,
      );

      if (response.previewUrl) {
        const previewURL = response.previewUrl;
        console.log('WAS Preview URL:', previewURL);
        // Open the WAS preview in a new tab
        window.open(previewURL, '_blank');
      } else {
        throw new Error('No preview URL received from server');
      }
    } catch (error) {
      console.error('Failed to get WAS preview URL:', error);
      setSnackbar({
        open: true,
        message: 'Failed to launch WAS Preview. Please try again.',
        severity: 'error',
      });
    }

    // reset loading indicators
    setIsLoading(false);
    if (isOriginal) {
      setWipPreviewBtnEnable(true);
    } else {
      setCurrentPreviewBtnEnable(true);
    }
  };

  const handleEditQuestionInlineXMLClick = () => {
    if (!issueData?.jiraId || !issueData?.wasQuestionId) {
      console.error('Missing required data: jiraId or wasQuestionId');
      setSnackbar({
        open: true,
        message: 'Missing required data: jiraId or wasQuestionId',
        severity: 'error',
      });
      return;
    }
    navigate(`/question/${issueData.wasQuestionId}/jira/${issueData.jiraId}/xml`);
  };

  const handleEQATClick = () => {
    if (!issueData) return;

    console.log('Launching EQAT for question:', issueData);

    if (!issueData.questionDetails || Object.keys(issueData.questionDetails).length === 0 || !issueData.productDetails
      || Object.keys(issueData.productDetails).length === 0) {
      console.error('Insufficient question data to launch EQAT');
      setSnackbar({
        open: true,
        message: 'Failed to launch EQAT. Incomplete question and product data.',
        severity: 'error',
      });
      return;
    }

    // Construct the EQAT URL with question parameters
    const token = getToken();
    const baseUrl = env.EQAT_LAUNCH_URL;
    if (token != null) {
      const encodedToken = btoa(token);
      const questionParams = `?i=${issueData.jiraId}&q=${issueData.wasQuestionId}&token=${encodedToken}`;
      const fullUrl = `${baseUrl}/open-question${questionParams}`;

      console.log(fullUrl);

      // Open the desktop app with the constructed URL
      window.location.href = fullUrl;
    } else {
      console.error('No token found');
      setSnackbar({
        open: true,
        message: 'Failed to launch EQAT. Authentication token not found.',
        severity: 'error',
      });
    }
  };

  const handleValidateQuestionClick = () => {
    setValidateQuestionBtnEnable(false);
    setIsLoading(true);

    if (!issueData) return;

    console.log('Launching Question Validator for question:', issueData.jiraId);

    if (!issueData.jiraId) {
      console.error('Insufficient issue data to launch Question Validator');
      setSnackbar({
        open: true,
        message: 'Failed to launch Question Validator. Missing JIRA ID.',
        severity: 'error',
      });
      return;
    }

    // Construct the Question Validator URL with question parameters
    const token = getToken();
    const baseUrl = env.EQAT_LAUNCH_URL;
    if (token != null) {
      // TODO: Uncomment this when the token is ready
      // const encodedToken = btoa(token);
      const validatorUrl = `jira/${issueData.jiraId}/validate-in-eqat?token=${token}`;
      console.log(validatorUrl);
      // const validatorUrl = `/jira/${issueData.jiraId}/validate-in-eqat`;
      const fullUrl = `${baseUrl}/${validatorUrl}`;

      console.log(fullUrl);

      // Open the desktop app with the constructed URL
      window.location.href = fullUrl;
    } else {
      console.error('No token found');
      setSnackbar({
        open: true,
        message: 'Failed to launch EQAT. Authentication token not found.',
        severity: 'error',
      });
    }

    setValidateQuestionBtnEnable(true);
    setIsLoading(false);
  };

  // Helper to format timestamp to mm/dd/yyyy
  function formatDate(dateValue: string | number) {
    const num = typeof dateValue === 'string' ? Number(dateValue) : dateValue;
    if (!num || isNaN(num)) return 'N/A';
    const date = new Date(num);
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  }

  /** Format seconds as human-readable (e.g. "1d", "7h", "30m") for consistent time tracking display. */
  function formatSecondsToReadable(seconds: number): string {
    if (seconds <= 0) return '0m';
    const d = Math.floor(seconds / 86400);
    const h = Math.floor((seconds % 86400) / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const parts: string[] = [];
    if (d > 0) parts.push(`${d}d`);
    if (h > 0) parts.push(`${h}h`);
    if (m > 0 || parts.length === 0) parts.push(`${m}m`);
    return parts.join(' ');
  }

  // format humn readable for time to seconds
  function formatHumanReadableToSeconds(humanReadable: string): number {
    const parts = humanReadable.split(' ');
    let seconds = 0;
    for (const part of parts) {
      const value = parseInt(part);
      const unit = part.replace(value.toString(), '');
      switch (unit) {
        case 'd':
          seconds += value * 86400;
          break;
        case 'h':
          seconds += value * 3600;
          break;
        case 'm':
          seconds += value * 60;
          break;
        default:
          seconds += value;
          break;
      }
    }
    return seconds;
  }

  // Use time tracking from response when available
  const timeTrackingFromApi = issueData?.timeTracking;
  const timeTrackingAbsolute = timeTrackingFromApi
    ? {
      originalEstimateSeconds: timeTrackingFromApi.originalEstimateSeconds ?? 0,
      remainingEstimateSeconds: timeTrackingFromApi.remainingEstimateSeconds ?? 0,
      timeSpentSeconds: timeTrackingFromApi.timeSpentSeconds ?? 0,
    }
    : null;
  const timeTrackingMaxAbsolute = timeTrackingAbsolute
    ? Math.max(
      timeTrackingAbsolute.originalEstimateSeconds,
      timeTrackingAbsolute.remainingEstimateSeconds,
      timeTrackingAbsolute.timeSpentSeconds,
      1
    )
    : 1;

  const timeTracking = timeTrackingFromApi
    ? {
      estimatedSeconds: formatHumanReadableToSeconds(timeTrackingFromApi.originalEstimate ?? '0'),
      remainingSeconds: formatHumanReadableToSeconds(timeTrackingFromApi.remainingEstimate ?? '0'),
      loggedSeconds: formatHumanReadableToSeconds(timeTrackingFromApi.timeSpent ?? '0'),
    }
    : null;

  const timeTrackingMax = timeTracking
    ? Math.max(
      timeTracking.estimatedSeconds,
      timeTracking.remainingSeconds,
      timeTracking.loggedSeconds,
      1
    )
    : 1;

  const hasTimeTracking = Boolean(timeTrackingFromApi);
  const shouldShowTimeTracking = hasTimeTracking && featureFlagTimeTracking;

  const TimeTrackingSection = () =>
    !timeTrackingFromApi ? null : (
      <Box sx={{ mb: 2, mx: 2, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
        <Box
          onClick={() => setTimeTrackingExpanded((e) => !e)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            '&:hover': { opacity: 0.85 },
          }}
        >
          <IconButton size="small" sx={{ p: 0, mr: 0.5 }} aria-label="Toggle time tracking">
            {timeTrackingExpanded ? (
              <KeyboardArrowDownIcon sx={{ color: 'grey.700' }} />
            ) : (
              <KeyboardArrowRightIcon sx={{ color: 'grey.700' }} />
            )}
          </IconButton>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, color: 'primary.dark' }}
          >
            Time Tracking
          </Typography>
        </Box>
        <Collapse in={timeTrackingExpanded}>
          <Box sx={{ p: 3, width: '100%', minWidth: 280 }}>
            {[
              { label: 'Estimated', value: timeTrackingFromApi.originalEstimate, key: 'estimated' },
              { label: 'Remaining', value: timeTrackingFromApi.remainingEstimate, key: 'remaining' },
              { label: 'Logged', value: timeTrackingFromApi.timeSpent, key: 'logged' },
            ].map(({ label, value, key }) => {
              const secs = formatHumanReadableToSeconds(
                value != null && String(value).trim() !== '' ? String(value) : '0',
              );
              return (
              <Box
                key={key}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  mb: 1,
                }}
              >
                <Typography variant="body2" sx={{ minWidth: 80 }}>
                  {label}
                </Typography>
                {
                  key === 'remaining' ? (
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(100, 100 - (secs / timeTrackingMax) * 100)}
                      sx={{
                        flex: 1,
                        height: 8,
                        width: 150,
                        bgcolor: 'orange',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: 'grey.200',
                        },
                      }}
                    />
                  ) : (
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(100, (secs / timeTrackingMax) * 100)}
                      sx={{
                        flex: 1,
                        height: 8,
                        width: 150,
                        bgcolor: 'grey.200',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: key === 'logged' ? 'success.main' : key === 'remaining' ? 'orange' : 'primary.main',
                        },
                      }}
                    />
                  )
                }
                <Typography variant="body2" sx={{ width: 50, textAlign: 'right' }}>
                  {formatFieldDisplay(value)}
                </Typography>
              </Box>
            );
            })}
          </Box>
        </Collapse>
      </Box>
    );

  const AbsoluteTimeTrackingSection = () =>
    !timeTrackingMaxAbsolute ? null : (
      <Box sx={{ mb: 2, mx: 2, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
        <Box
          onClick={() => setTimeAbsoluteTrackingExpanded((e) => !e)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            '&:hover': { opacity: 0.85 },
          }}
        >
          <IconButton size="small" sx={{ p: 0, mr: 0.5 }} aria-label="Toggle absolute time tracking">
            {timeAbsoluteTrackingExpanded ? (
              <KeyboardArrowDownIcon sx={{ color: 'grey.700' }} />
            ) : (
              <KeyboardArrowRightIcon sx={{ color: 'grey.700' }} />
            )}
          </IconButton>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, color: 'primary.dark' }}
          >
            Absolute Time Tracking
          </Typography>
        </Box>
        <Collapse in={timeAbsoluteTrackingExpanded}>
          <Box sx={{ p: 3, width: '100%', minWidth: 280 }}>
            {[
              { label: 'Estimated', value: timeTrackingFromApi?.originalEstimateSeconds ?? 0, key: 'estimated' },
              {
                label:
                  (timeTrackingFromApi?.remainingEstimateSeconds ?? 0) > (timeTrackingFromApi?.originalEstimateSeconds ?? 0)
                    ? 'Overdue'
                    : 'Remaining',
                value: timeTrackingFromApi?.remainingEstimateSeconds ?? 0,
                key: 'remaining',
              },
              { label: 'Logged', value: timeTrackingFromApi?.timeSpentSeconds ?? 0, key: 'logged' },
            ].map(({ label, value, key }) => (
              <Box
                key={key}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  mb: 1,
                }}
              >
                <Typography variant="body2" sx={{ minWidth: 80 }}>
                  {label}
                </Typography>
                {
                  key === 'remaining' ? (
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(100, 100 - (value / timeTrackingMaxAbsolute) * 100)}
                      sx={{
                        flex: 1,
                        height: 8,
                        width: 150,
                        bgcolor: 'orange',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: 'grey.200',
                        },
                      }}
                    />
                  ) : (
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(100, (value / timeTrackingMaxAbsolute) * 100)}
                      sx={{
                        flex: 1,
                        height: 8,
                        width: 150,
                        bgcolor: 'grey.200',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: key === 'logged' ? 'success.main' : key === 'remaining' ? 'orange' : 'primary.main',
                        },
                      }}
                    />
                  )
                }
                <Typography variant="body2" sx={{ width: 50, textAlign: 'right' }}>
                  {formatSecondsToReadable(value)}
                </Typography>
              </Box>
            ))}
          </Box>
        </Collapse>
      </Box>
    );


  if (loading || !issueData) {
    // Skeleton loader for the card
    return (
      <Box sx={{ p: { xs: 1.5, sm: 3 }, maxWidth: 1200, margin: '0 auto' }}>
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Skeleton variant="rectangular" width={24} height={24} sx={{ mr: 0.5 }} />
            <Skeleton variant="text" width={180} height={24} />
          </Box>
          <Box sx={{ pl: 3 }}>
            {[1, 2, 3].map((i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Skeleton variant="text" width={80} height={20} />
                <Skeleton variant="rectangular" width="100%" height={8} sx={{ flex: 1, borderRadius: 1 }} />
                <Skeleton variant="text" width={56} height={20} />
              </Box>
            ))}
          </Box>
        </Box>
        <Paper sx={{ p: { xs: 1.5, sm: 3 }, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Skeleton
              variant="rectangular"
              width={75}
              height={75}
              sx={{ mr: 2, borderRadius: '1px' }}
            />
            <Skeleton variant="text" width={400} height={48} />
          </Box>
          <TableContainer>
            <Table>
              <TableBody>
                {[...Array(7)].map((_, i) => (
                  <TableRow key={i}>
                    <TableCell sx={{ width: '20%' }}>
                      <Skeleton variant="text" width={100} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width="80%" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
        <Paper sx={{ p: { xs: 1.5, sm: 3 }, mb: 3 }}>
          <Skeleton variant="text" width={180} height={32} sx={{ mb: 2 }} />
          <TableContainer>
            <Table>
              <TableBody>
                {[...Array(4)].map((_, i) => (
                  <TableRow key={i}>
                    <TableCell sx={{ width: '20%' }}>
                      <Skeleton variant="text" width={120} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width="80%" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
        <Paper sx={{ p: { xs: 1.5, sm: 3 } }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-around',
              gap: 4,
              flexWrap: 'wrap',
              alignItems: 'flex-start',
            }}
          >
            {[...Array(4)].map((_, i) => (
              <Box
                key={i}
                sx={{
                  border: '1px solid #ccc',
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  minWidth: 130,
                }}
              >
                <Skeleton
                  variant="text"
                  width={100}
                  height={28}
                  sx={{ mb: 1 }}
                />
                <Skeleton
                  variant="rectangular"
                  width={130}
                  height={36}
                  sx={{ mb: 1 }}
                />
                <Skeleton variant="rectangular" width={130} height={36} />
              </Box>
            ))}
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', lg: 'row' },
        gap: 2,
        justifyContent: 'center',
        alignItems: { xs: 'stretch', lg: 'flex-start' },
        width: '100%',
      }}
    >
      {/* Issue details card */}
      <Box>

        {/* Mobile time tracking button */}
        {shouldShowTimeTracking && isSmallScreen && (
          <Box sx={{ mr: 2, display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button variant="outlined" onClick={() => setTimeTrackingDrawerOpen(true)}>
              Open Time Tracking
            </Button>
          </Box>
        )}

        {/* Title Section */}
        <Paper sx={{ p: { xs: 1.5, sm: 3 }, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1.5 }}>
            {/* <Avatar> I</Avatar> */}
            <Box
              sx={{
                width: { xs: 52, sm: 75 },
                height: { xs: 52, sm: 75 },
                bgcolor: '#90A4AE',
                color: '#fff',
                borderRadius: '1px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 700,
                mr: { xs: 1, sm: 2 },
                boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.25)',
                flexShrink: 0, // Prevent shrinking
              }}
            >
              I
            </Box>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                color: '#333',
                textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                overflowWrap: 'anywhere',
              }}
            >
              {formatFieldDisplay(
                issueData.title?.trim()
                  ? issueData.title
                  : issueData.questionDetails?.ngQuestionTitle?.trim()
                    ? issueData.questionDetails.ngQuestionTitle
                    : issueData.questionDetails?.nQuestionTitle?.trim()
                      ? issueData.questionDetails.nQuestionTitle
                      : null,
              )}
            </Typography>
          </Box>

          {/* Details Table */}
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table
              sx={{
                minWidth: { xs: 640, sm: 'auto' },
                '& .MuiTableCell-root': { border: '1px solid #e0e0e0' },
              }}
            >
              <TableBody>
                <TableRow>
                  <TableCell
                    sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold', width: '20%' }}
                  >
                    JIRA ID
                  </TableCell>
                  <TableCell>
                    {(jiraIdFromParams != null && jiraIdFromParams !== '' && issueData.jiraId !== jiraIdFromParams) ? (
                      <>
                        {formatFieldDisplay(jiraIdFromParams)}
                        <Chip
                          label={`Jira ID Mismatch with ${formatFieldDisplay(issueData.jiraId)}.Please check for the duplicate JIRA tickets.`}
                          color="warning"
                          size="small"
                          sx={{ ml: 1 }}
                        />
                      </>
                    ) : (
                      <>{formatFieldDisplay(issueData.jiraId)}</>
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>
                    Case ID
                  </TableCell>
                  <TableCell>{formatFieldDisplay(issueData.caseId)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>
                    WAS Master Question ID
                  </TableCell>
                  <TableCell>{formatFieldDisplay(issueData.wasQuestionId)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>
                    Product Title
                  </TableCell>
                  <TableCell>{formatFieldDisplay(issueData.productDetails?.title)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>
                    Native Product Title
                  </TableCell>
                  <TableCell>
                    {formatFieldDisplay(
                      (
                        [
                          issueData.questionProductDetails?.season,
                          issueData.questionProductDetails?.platform,
                          issueData.questionProductDetails?.title,
                        ].filter(Boolean) as string[]
                      ).join(' ').trim() || null,
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>
                    Product UUID
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {formatFieldDisplay(issueData.productDetails?.productUuid)}
                      {issueData.productDetails?.isAlternativeProduct && (
                        <Chip
                          icon={<WarningAmberIcon />}
                          label="This product is not onboarded. All operations are proceeding with this alternative product."
                          color="warning"
                          size="small"
                          sx={{ ml: 1 }}
                        />
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>
                    Status
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                      <Typography>
                        {issueData.status === 'Open'
                          ? 'OPEN'
                          : issueData.status
                            ? 'CLOSE'
                            : 'N/A'}
                      </Typography>
                      {(hasPermission(PERMISSIONS.UPDATE_ISSUE_STATUS)) && (
                        <Tooltip title={issueData.status === 'Open' ? 'Close Issue' : 'Open Issue'}>
                          <Button
                            variant="contained"
                            sx={{
                              ml: 1,
                              height: 24,
                              minWidth: 60,
                              p: 0,
                              fontSize: 13,
                              boxShadow: 'none',
                            }}
                            onClick={handleStatusButtonClick}
                          >
                            {issueData.status === 'Open' ? 'CLOSE' : 'OPEN'}
                          </Button>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>
                    Issue ID
                  </TableCell>
                  <TableCell>{formatFieldDisplay(issueData.issueId)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>
                    Authored By Vendor
                  </TableCell>
                  <TableCell>{formatFieldDisplay(issueData.authoredByVendor)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>
                    Created At
                  </TableCell>
                  <TableCell>{formatDate(issueData.createdAt)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>
                    Updated At
                  </TableCell>
                  <TableCell>{formatDate(issueData.updatedAt)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Question Details Section */}
        <Paper sx={{ p: { xs: 1.5, sm: 3 }, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
            Question Details
          </Typography>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table
              sx={{
                minWidth: { xs: 640, sm: 'auto' },
                '& .MuiTableCell-root': { border: '1px solid #e0e0e0' },
              }}
            >
              <TableBody>
                <TableRow>
                  <TableCell
                    sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold', width: '20%' }}
                  >
                    Question Name
                  </TableCell>
                  <TableCell>{formatFieldDisplay(issueData.questionDetails?.ngQuestionTitle || issueData.questionDetails?.nQuestionTitle)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>
                    Question Module Title
                  </TableCell>
                  <TableCell>{formatFieldDisplay(issueData.questionDetails?.ngModuleTitle)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>
                    Question Type
                  </TableCell>
                  <TableCell>
                    {formatFieldDisplay(getQuestionTypeName(issueData.questionDetails?.nTypeId))}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>
                    NextGen EPROF
                  </TableCell>
                  <TableCell>
                    {formatFieldDisplay(issueData.productDetails?.eprof)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>
                    Native Section Title
                  </TableCell>
                  <TableCell>
                    {formatFieldDisplay(issueData.questionDetails?.ngSectionTitle)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>
                    EQAT ID
                  </TableCell>
                  <TableCell>
                    {formatFieldDisplay(issueData.questionDetails?.qtiId)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>
                    WAS Question Card ID
                  </TableCell>
                  <TableCell>
                    {formatFieldDisplay(issueData.questionDetails?.ngQuestionId)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>
                    Restriction
                  </TableCell>
                  <TableCell>
                    {formatFieldDisplay(getQuestionRestrictionLabel(issueData.questionDetails?.restriction))}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>
                    SVN Location
                  </TableCell>
                  <TableCell>
                    {issueData.questionDetails?.svnProduct?.trim() ? (
                      <Link href={issueData.questionDetails.svnProduct.trim()} target="_blank" rel="noopener noreferrer">
                        {issueData.questionDetails.svnProduct.trim()}
                      </Link>
                    ) : (
                      formatFieldDisplay(null)
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>
                    ZIP Location
                  </TableCell>
                  <TableCell>
                    {formatFieldDisplay(issueData.questionDetails?.zipName)}
                  </TableCell>
                </TableRow>
                {/* <TableRow>
                <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>
                  Corrections Made(SVN)
                </TableCell>
                <TableCell>
                  {issueData.svnVersion}
                </TableCell>
              </TableRow> */}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Action Buttons */}
        <Paper sx={{ p: { xs: 1.5, sm: 3 } }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: { xs: 'stretch', md: 'space-around' },
              gap: { xs: 1.5, sm: 2, md: 4 },
              flexWrap: 'wrap',
              alignItems: 'stretch',
            }}
          >
            {/* Was Preview Box */}
            {(hasPermission(PERMISSIONS.WAS_PREVIEW) || hasPermission(PERMISSIONS.WAS_PREVIEW)) &&
              <Box
                sx={{
                  border: '1px solid #ccc',
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  minWidth: { xs: '100%', sm: 170, md: 130 },
                  flex: { xs: '1 1 100%', sm: '1 1 170px', md: '0 0 auto' },
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                  WAS PREVIEW
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
                  {hasPermission(PERMISSIONS.WAS_PREVIEW) &&
                    <Button variant="contained" sx={{ width: '100%' }} onClick={() => {
                      handleWasPreview(false);
                    }} disabled={!currentPreviewBtnEnable}>
                      Current
                    </Button>
                  }
                  {hasPermission(PERMISSIONS.WAS_PREVIEW) &&
                    <Button variant="contained" sx={{ width: '100%' }} onClick={() => {
                      handleWasPreview(true);
                    }} disabled={!wipPreviewBtnEnable}>
                      Original
                    </Button>
                  }
                </Box>
              </Box>
            }
            {/* Edit In Box */}
            {(hasPermission(PERMISSIONS.EDIT_QUESTION_EQAT) || hasPermission(PERMISSIONS.GET_QUESTION_XML)) && issueData?.status?.toLowerCase() === 'open' &&
              <Box
                sx={{
                  border: '1px solid #ccc',
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  minWidth: { xs: '100%', sm: 170, md: 130 },
                  flex: { xs: '1 1 100%', sm: '1 1 170px', md: '0 0 auto' },
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                  EDIT IN
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
                  {hasPermission(PERMISSIONS.EDIT_QUESTION_EQAT) &&
                    <Button variant="contained" sx={{ width: '100%' }} onClick={handleEQATClick}>
                      EQAT
                    </Button>
                  }
                  {hasPermission(PERMISSIONS.GET_QUESTION_XML) &&
                    <Button variant="contained" sx={{ width: '100%' }} disabled={false} onClick={handleEditQuestionInlineXMLClick}>
                      XML
                    </Button>
                  }
                </Box>
              </Box>
            }

            {/* Save To Box */}
            {(hasPermission(PERMISSIONS.SAVE_QUESTION_WAS) || hasPermission(PERMISSIONS.SAVE_QUESTION_SVN)) && issueData?.status?.toLowerCase() === 'open' &&
              <Box
                sx={{
                  border: '1px solid #ccc',
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  minWidth: { xs: '100%', sm: 170, md: 130 },
                  flex: { xs: '1 1 100%', sm: '1 1 170px', md: '0 0 auto' },
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                  SAVE TO
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
                  {hasPermission(PERMISSIONS.SAVE_QUESTION_SVN) && (
                    <Button variant="contained" sx={{ width: '100%' }} onClick={handleSaveToSVNClick} disabled={!svnSaveBtnEnable}>
                      SVN
                    </Button>
                  )}
                  {hasPermission(PERMISSIONS.SAVE_QUESTION_WAS) && (
                    <Tooltip
                      title={!getWASSaveButtonStatus() ? "Please save changes to SVN before proceeding with WAS." : ""}
                      arrow
                    >
                      <span>
                        <Button variant="contained" sx={{ width: '100%' }} onClick={handleSaveToWASClick}
                          disabled={!getWASSaveButtonStatus()}>
                          WAS
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            }

            {/* Discard Changes Box */}
            {hasPermission(PERMISSIONS.RELOAD_QUESTION_FROM_SVN) && issueData?.status?.toLowerCase() === 'open' &&
              <Box
                sx={{
                  border: '1px solid #ccc',
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  alignSelf: { xs: 'stretch', md: 'flex-start' },
                  minWidth: { xs: '100%', sm: 170, md: 130 },
                  flex: { xs: '1 1 100%', sm: '1 1 170px', md: '0 0 auto' },
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                  DISCARD CHANGES
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
                  <Button variant="contained" sx={{ width: '100%' }} onClick={handleReloadSVNClick} disabled={!svnReloadBtnEnable}>
                    Reload from SVN
                  </Button>
                </Box>
              </Box>
            }

            {/* Validator Box */}
            {hasPermission(PERMISSIONS.GET_VALIDATED_QUESTION_PACKAGE) && featureFlagValidator && issueData?.status?.toLowerCase() === 'open' &&
              <Box
                sx={{
                  border: '1px solid #ccc',
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  alignSelf: { xs: 'stretch', md: 'flex-start' },
                  minWidth: { xs: '100%', sm: 170, md: 130 },
                  flex: { xs: '1 1 100%', sm: '1 1 170px', md: '0 0 auto' },
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                  QUESTION VALIDATOR
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
                  <Button variant="contained" sx={{ width: '100%' }} onClick={handleValidateQuestionClick}
                    disabled={!validateQuestionBtnEnable}>
                    Validate
                  </Button>
                </Box>
              </Box>
            }
          </Box>
        </Paper>

        {/* Status Update Dialog */}
        <Dialog open={isDialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth>
          <DialogTitle>Confirm Status Update</DialogTitle>
          <DialogContent>
            <Typography sx={{ mb: 2 }}>
              Are you sure you want to change the status of this issue
              from <strong>"{formatFieldDisplay(issueData.status)}"</strong> to <strong>"{issueData.status === 'Open' ? 'Close' : 'Open'}"</strong>?
            </Typography>
            {isUpdating && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  Updating status...
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogClose} disabled={isUpdating}>
              Cancel
            </Button>
            <Button
              onClick={handleStatusUpdate}
              color="primary"
              variant="contained"
              disabled={isUpdating}
              startIcon={isUpdating ? <CircularProgress size={20} /> : undefined}
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for status updates */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/*  Loading indicator */}
        {
          isLoading && <LoadingIndicator showOverlay={true} disableOutsideClick={true} />
        }

        {/* Confirmation Dialogs */}
        <ConfirmationDialog
          open={confirmSaveToSVN}
          title="Save Changes Confirmation"
          message="Are you sure you want to save changes to SVN? This action cannot be undone."
          confirmText="Save to SVN"
          cancelText="Cancel"
          onConfirm={handleSaveToSVN}
          onCancel={() => setConfirmSaveToSVN(false)}
          confirmButtonColor="primary"
          disabled={isLoading}
        />

        <ConfirmationDialog
          open={confirmSaveToWAS}
          title="Confirm Save to WAS"
          message="Please make sure to save changes to SVN before proceeding. Are you sure you want to save changes to WAS? This action cannot be undone."
          confirmText="Save to WAS"
          cancelText="Cancel"
          onConfirm={handleSaveToWAS}
          onCancel={() => setConfirmSaveToWAS(false)}
          confirmButtonColor="primary"
          disabled={isLoading}
        />

        <ConfirmationDialog
          open={confirmReloadSVN}
          title="Confirm Reload from SVN"
          message="Are you sure you want to reload this question from SVN? This will discard any unsaved changes."
          confirmText="Reload from SVN"
          cancelText="Cancel"
          onConfirm={handleReloadSVN}
          onCancel={() => setConfirmReloadSVN(false)}
          confirmButtonColor="primary"
          disabled={isLoading}
        />

      </Box>

      {/* Time Tracking - desktop panel */}
      {shouldShowTimeTracking && !isSmallScreen && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: 360, flexShrink: 0 }}>
          <Paper sx={{ p: 2 }}>
            <AbsoluteTimeTrackingSection />
          </Paper>
          <Paper sx={{ p: 2 }}>
            <TimeTrackingSection />
          </Paper>
        </Box>
      )}

      {/* Time Tracking - mobile drawer */}
      {shouldShowTimeTracking && isSmallScreen && (
        <Drawer
          anchor="right"
          open={timeTrackingDrawerOpen}
          onClose={() => setTimeTrackingDrawerOpen(false)}
          PaperProps={{
            sx: { width: 'min(90vw, 360px)' },
          }}
        >
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Time Tracking
            </Typography>
            <Button size="small" onClick={() => setTimeTrackingDrawerOpen(false)}>
              Close
            </Button>
          </Box>
          <Box sx={{ p: 2, pt: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Paper sx={{ p: 2 }}>
              <AbsoluteTimeTrackingSection />
            </Paper>
            <Paper sx={{ p: 2 }}>
              <TimeTrackingSection />
            </Paper>
          </Box>
        </Drawer>
      )}

    </Box>
  );
};

export default IssueDetailsCard;
